<template>
  <div
    ref="root"
    class="meridia-tab-panel-content flex flex-col flex-1 overflow-hidden min-h-0"
    v-bind="getPt('root', passThrough)"
  >
    <slot />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onActivated } from 'vue';
import { usePassThrough } from 'primevue/passthrough';
import type { PassThrough } from '@primevue/core';
import { getPt } from 'src/design_system/components/utils';
import type {
  MeridiaTabPanelContentProps,
  MeridiaTabPanelContentPassThroughOptions,
} from 'src/design_system/components/tab/types';

defineOptions({ name: 'MeridiaTabPanelContent' });

const props = defineProps<MeridiaTabPanelContentProps>();

defineSlots<{ default: () => any }>();

const root = ref<HTMLElement>();

const passThrough = computed(() =>
  usePassThrough(
    props.pt,
    {} as MeridiaTabPanelContentPassThroughOptions,
    { mergeSections: true, mergeProps: true }
  ) as PassThrough<MeridiaTabPanelContentPassThroughOptions>
);

// Force CSS animation re-trigger when restored from KeepAlive cache.
// A synchronous reflow between clearing and restoring the animation ensures
// the browser recognizes it as a new animation. Async alternatives (nextTick,
// requestAnimationFrame) are less reliable across browsers.
onActivated(() => {
  const el = root.value;
  if (el) {
    el.style.animation = 'none';
    void el.offsetHeight; // Synchronous reflow — triggers style recalculation
    el.style.animation = '';
  }
});
</script>

<style lang="scss" scoped>
@keyframes slideInFromRight {
  from {
    opacity: 0;
    transform: translateX(50px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes slideInFromLeft {
  from {
    opacity: 0;
    transform: translateX(-50px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.tab-enter-from-right {
  animation: slideInFromRight 250ms ease-out;
}

.tab-enter-from-left {
  animation: slideInFromLeft 250ms ease-out;
}

.tab-enter-fade {
  animation: fadeIn 250ms ease-out;
}
</style>
